import { useEffect, useRef, useState } from 'react';

import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import type { CommunityReactionType } from '~/interfaces/community';
import { NectarPageBlock } from '~/utils/nectarPageBlock';
import { withNectarTrackingParams } from '~/utils/nectarTracking';

import { useCommunityMembership } from '../../../_hooks/useCommunityMembership';
import { useCommunityPostOptimistic } from '../../../_hooks/useCommunityPostOptimistic';
import type { CommunityPostsPage } from '../../../_hooks/useCommunityPosts';
import { communityPostsKeys } from '../../../_hooks/useCommunityPosts';

type ReactableType = 'CommunityPost' | 'CommunityPostComment';

type ReactionStateResponse = {
  reactions_count: Partial<Record<CommunityReactionType, number>>;
  current_member_reactions: CommunityReactionType[];
};

type UseReactableReactionsArgs = {
  reactableType: ReactableType;
  reactableId: string;
  reactionsCount: Partial<Record<CommunityReactionType, number>>;
  memberReactions: CommunityReactionType[];
};

const memberReactionsSignature = (member: CommunityReactionType[]) => [...member].sort().join(',');

const countsSignature = (counts: Partial<Record<CommunityReactionType, number>>) => {
  const entries = (Object.keys(counts) as CommunityReactionType[])
    .filter((key) => (counts[key] ?? 0) !== 0)
    .sort()
    .map((key) => `${key}:${counts[key] ?? 0}`);
  return entries.join('|');
};

const toggleMemberReactions = (
  member: CommunityReactionType[],
  reactionType: CommunityReactionType,
): CommunityReactionType[] => {
  if (member.includes(reactionType)) {
    return member.filter((type) => type !== reactionType);
  }
  return [...member, reactionType];
};

const toggleReactionCounts = (
  counts: Partial<Record<CommunityReactionType, number>>,
  reactionType: CommunityReactionType,
  adding: boolean,
): Partial<Record<CommunityReactionType, number>> => {
  const next: Partial<Record<CommunityReactionType, number>> = { ...counts };
  const current = next[reactionType] ?? 0;
  if (adding) {
    next[reactionType] = current + 1;
    return next;
  }

  const nextValue = current - 1;
  if (nextValue <= 0) {
    delete next[reactionType];
  } else {
    next[reactionType] = nextValue;
  }
  return next;
};

export const useReactableReactions = ({
  reactableType,
  reactableId,
  reactionsCount,
  memberReactions,
}: UseReactableReactionsArgs) => {
  const fetcher = useNonRevalidatingFetcher<ReactionStateResponse>();
  const { permissions, openJoinModal } = useCommunityMembership();
  const { applyOptimisticPatch } = useCommunityPostOptimistic();
  const queryClient = useQueryClient();
  const latestOperationIdRef = useRef(0);

  const [localCounts, setLocalCounts] = useState(() => ({ ...reactionsCount }));
  const [localMember, setLocalMember] = useState(() => [...memberReactions]);

  const serverSyncKey = `${reactableId}|${countsSignature(reactionsCount)}#${memberReactionsSignature(memberReactions)}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: serverSyncKey serializes server props so we do not reset on unrelated parent re-renders or during optimistic updates
  useEffect(() => {
    setLocalCounts({ ...reactionsCount });
    setLocalMember([...memberReactions]);
  }, [serverSyncKey]);

  const handleReactionClick = async (reactionType: CommunityReactionType) => {
    if (!permissions.canReact) {
      if (permissions.isGuest) openJoinModal();
      return;
    }

    const adding = !localMember.includes(reactionType);
    const operationId = latestOperationIdRef.current + 1;
    latestOperationIdRef.current = operationId;

    const prevMember = localMember;
    const prevCounts = localCounts;
    const nextMember = toggleMemberReactions(localMember, reactionType);
    const nextCounts = toggleReactionCounts(localCounts, reactionType, adding);

    const rollback = () => {
      setLocalMember(prevMember);
      setLocalCounts(prevCounts);
      if (reactableType === 'CommunityPost') {
        applyOptimisticPatch(reactableId, {
          reactions_count: prevCounts,
          current_member_reactions: prevMember,
        });
      }
    };

    setLocalMember(nextMember);
    setLocalCounts(nextCounts);

    if (reactableType === 'CommunityPost') {
      applyOptimisticPatch(reactableId, {
        reactions_count: nextCounts,
        current_member_reactions: nextMember,
      });
    }

    const applyServerState = (data: ReactionStateResponse | undefined) => {
      const serverCounts = data?.reactions_count ?? nextCounts;
      const serverMember = data?.current_member_reactions ?? nextMember;
      setLocalCounts(serverCounts);
      setLocalMember(serverMember);

      if (reactableType !== 'CommunityPost') return;

      queryClient.setQueriesData<InfiniteData<CommunityPostsPage>>(
        { queryKey: communityPostsKeys.all },
        (old) =>
          old && {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              posts: page.posts.map((post) =>
                post.id === reactableId
                  ? { ...post, reactions_count: serverCounts, current_member_reactions: serverMember }
                  : post,
              ),
            })),
          },
      );
      applyOptimisticPatch(reactableId, {
        reactions_count: serverCounts,
        current_member_reactions: serverMember,
      });
    };

    const formData = new FormData();
    formData.append('reactable_type', reactableType);
    formData.append('reactable_id', reactableId);
    formData.append('reaction_type', reactionType);

    if (adding) {
      withNectarTrackingParams(formData, NectarPageBlock.CommunityPostReactionButton);
    } else {
      formData.append('intent', 'destroy');
    }

    const { ok, status, data } = await fetcher.submit(formData, {
      method: 'POST',
      action: '/community_reaction',
    });

    if (latestOperationIdRef.current !== operationId) return;

    const succeeded = adding ? status === 201 : status === 200 || status === 204;
    if (!(ok && succeeded)) {
      rollback();
      return;
    }

    applyServerState(data);
  };

  const reactionEntries = Object.entries(localCounts)
    .filter(([, count]) => (count ?? 0) > 0)
    .map(([type, count]) => ({ type: type as CommunityReactionType, count: count ?? 0 }));

  const totalReactions = reactionEntries.reduce((sum, reaction) => sum + reaction.count, 0);

  return {
    permissions,
    localMember,
    reactionEntries,
    totalReactions,
    handleReactionClick,
  };
};
